import { AttributeFilters, Coordinates, Site } from '@cinch-labs/sites-sdk'
import { Env, readFromEnv } from '@cinch-nx/environments'
import { getSites } from '@cinch-nx/shared-sites'
import useSWRImmutable from 'swr/immutable'
import axios from 'axios'
import { useState } from 'react'
import { ListedSite } from './find-a-site'

const httpClient = axios.create()

interface UseGetSitesProps {
  postcode?: string
  filters: AttributeFilters
  setMobileSelectedSite: (site: ListedSite) => void
}

export interface UseGetSitesResult {
  validPostcode: string
  coordinates: Coordinates
  sites: ListedSite[]
}

export const useGetSites = ({
  postcode,
  filters,
  setMobileSelectedSite,
}: UseGetSitesProps) => {
  const { data, isLoading, error } = useSWRImmutable<UseGetSitesResult>(
    postcode
      ? ['get-mapped-sites', postcode, filters, setMobileSelectedSite]
      : null,
    async ([, _postcode, _filters, _setMobileSelectedSite]: [
      string,
      string,
      AttributeFilters | undefined,
      (site: ListedSite) => void,
    ]) => {
      const coordinates = await getPostcodeCoordinates(_postcode)
      if (!coordinates) throw new Error('error')

      const sites = await getSites(_filters)

      if (sites.length === 0) throw new Error('error')

      const sortedSites = mapAndSortSites(sites, coordinates)
      _setMobileSelectedSite(sortedSites[0])

      return {
        validPostcode: _postcode,
        coordinates,
        sites: sortedSites,
      }
    },
    { errorRetryCount: 1 },
  )

  return { data, isLoading, error }
}

export const useSitesToShow = (sites: ListedSite[]) => {
  const [numSitesToShow, setNumSitesToShow] = useState<number>(3)

  const showMoreSites = () => setNumSitesToShow(numSitesToShow + 3)

  return { sitesToShow: sites.slice(0, numSitesToShow), showMoreSites }
}

const getPostcodeCoordinates = async (
  postcode: string,
): Promise<Coordinates | undefined> => {
  const getCoordinatesUrl = `${readFromEnv(
    Env.LogisticsRoutingServiceUrl,
  )}/coordinates`

  try {
    const response = await httpClient.request<Coordinates>({
      url: getCoordinatesUrl,
      method: 'GET',
      params: {
        postcode,
      },
    })

    return response.data
  } catch {
    return
  }
}

const mapAndSortSites = (
  sites: Site[],
  coordinates: Coordinates,
): ListedSite[] => {
  const listedSites: ListedSite[] = []
  for (const site of sites) {
    const siteCoords = site.getFeatureAttribute('coordinates')

    if (!siteCoords) continue

    const distance = getDistance(coordinates, siteCoords)

    const address = site.getFeatureAttribute('address')

    if (!address) continue

    // update this to get the correct name for the type of site
    const siteName =
      site.getFeatureAttribute('store_name') ??
      site.getFeatureAttribute('display_name')

    if (!siteName) continue

    listedSites.push({
      siteId: site.siteId,
      siteName,
      address,
      coordinates: siteCoords,
      distance,
      site,
    })
  }

  listedSites.sort((a, b) => a.distance - b.distance)

  return listedSites
}
/**
 * @see https://en.wikipedia.org/wiki/Haversine_formula
 * @param first
 * @param second
 * @returns distance in meters
 */
export const getDistance = (
  first: Coordinates,
  second: Coordinates,
): number => {
  const EarthRadius = 6371e3 // metres
  const firstLatitudeRadians = (first.latitude * Math.PI) / 180 // φ, λ in radians
  const secondLatitudeRadians = (second.latitude * Math.PI) / 180
  const latitudeDeltaRadians =
    ((second.latitude - first.latitude) * Math.PI) / 180
  const longitudeDeltaRadians =
    ((second.longitude - first.longitude) * Math.PI) / 180

  const haversine =
    Math.sin(latitudeDeltaRadians / 2) * Math.sin(latitudeDeltaRadians / 2) +
    Math.cos(firstLatitudeRadians) *
      Math.cos(secondLatitudeRadians) *
      Math.sin(longitudeDeltaRadians / 2) *
      Math.sin(longitudeDeltaRadians / 2)
  const c = 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))

  const d = EarthRadius * c // in metres

  const milesPerMeter = 0.000_621_371
  return Math.round(d * milesPerMeter * 10) / 10
}
